import {
  IssueCaseDoc,
  IssueProduct,
  IssueSourceType,
  OrderDoc,
  OrderStatusCode,
  StoreDoc,
} from '@gooduncles/gu-app-schema';
import { FC, Fragment } from 'react';

import { formatNumber } from 'src/utils/common';
import {
  getIssueCaseLabel,
  isIssueCaseEditable,
  issueCaseOptions,
  parseOrderProductsToIssueProducts,
} from 'src/utils/issue/issue-util';
import { getOrderStatusKr, getStatusBadgeStatus } from 'src/utils/order-util';

import useIssueCaseActions from 'src/hooks/useIssueCaseActions';

import { AddButton } from 'src/atomic-components/atoms/AddButton';
import IssuePanelDivider from 'src/atomic-components/atoms/Divider/IssuePanelDivider';
import IssueCardHeader from 'src/atomic-components/atoms/IssueCardItem/IssueCardHeader';
import { InfoItem } from 'src/atomic-components/atoms/IssueInfoItem';
import { IssueStatusBadge } from 'src/atomic-components/atoms/IssueStatusBadge';
import { IssueProductItem } from 'src/atomic-components/molecules/IssueProductItem/IssueProductItem';
import LinkedMessagesSection from 'src/components/Issue/MessageLink/LinkedMessagesSection';

import AssignedIssueItem from '../AssignedIssueItem';
import IssueProductsEditModal from '../IssueProductsEditModal/IssueProductsEditModal';

interface IssueOrderCardProps {
  store: StoreDoc;
  order: OrderDoc;
  issueCases?: IssueCaseDoc[];
  onIssueCaseClick?: (issueCaseId: string) => void;
}

const IssueOrderCard: FC<IssueOrderCardProps> = ({ order, store, issueCases, onIssueCaseClick }) => {
  const showIssueCases = issueCases && issueCases.length > 0;
  const selectableProducts: IssueProduct[] = parseOrderProductsToIssueProducts(order.products ?? []);
  const canCreateIssue = [OrderStatusCode.ACCEPTED, OrderStatusCode.DELIVERED].includes(order.orderStatus);

  const { createNewIssueCase, updateIssueCaseProducts } = useIssueCaseActions({
    getSourceParams: () => ({
      sourceType: IssueSourceType.ORDER,
      sourceId: order._id,
      sourceReferenceDate: order.deliveryDate,
      storeId: store._id,
      storeName: store.storeNickname,
    }),
  });

  return (
    <div>
      <IssueCardHeader title='주문' />
      <>
        <InfoItem
          label='주문상태'
          value={
            <IssueStatusBadge
              text={getOrderStatusKr(order.orderStatus)}
              status={getStatusBadgeStatus(order.orderStatus)}
            />
          }
        />
        <InfoItem label='주문번호' value={order._id} />
        <InfoItem label='주문시각' value={order.orderDate} />
        <InfoItem
          label='총 주문금액'
          value={`${formatNumber(order.grandTotal ?? order.paidAmount)}원`}
          isHighlight={true}
        />
      </>
      <IssuePanelDivider />
      {showIssueCases && (
        <>
          {issueCases.map((issueCase) => (
            <Fragment key={issueCase._id}>
              <AssignedIssueItem
                label={getIssueCaseLabel(issueCase)}
                color='orange'
                onLabelClick={() => onIssueCaseClick?.(issueCase._id)}
                rightSection={
                  <IssueProductsEditModal
                    color='orange'
                    title={getIssueCaseLabel(issueCase)}
                    buttonText={issueCase.products ? `${issueCase.products.length}건` : '상품 등록'}
                    currentProducts={issueCase.products ?? []}
                    selectableProducts={selectableProducts}
                    editable={canCreateIssue && isIssueCaseEditable(issueCase)}
                    onUpdateProducts={(products) => updateIssueCaseProducts(issueCase, products)}
                  />
                }
              />
              <LinkedMessagesSection
                issueCase={issueCase}
                editable={canCreateIssue && isIssueCaseEditable(issueCase)}
              />
              {issueCase.products
                ? issueCase.products.map((p, index) => (
                    <IssueProductItem
                      key={issueCase._id + p.productId}
                      productName={p.productName}
                      unitPrice={p.price}
                      quantity={p.quantity}
                      totalPrice={p.price * p.quantity}
                      showHierarchy={true}
                      isLast={issueCase.products?.length ? index === issueCase.products.length - 1 : false}
                    />
                  ))
                : null}
            </Fragment>
          ))}

          <IssuePanelDivider />
        </>
      )}
      <AddButton
        text='새로운 이슈 케이스'
        color='orange'
        onSelect={createNewIssueCase}
        options={issueCaseOptions}
        disabled={!canCreateIssue}
      />
      <IssuePanelDivider />
      {order.products.map((product, index) => (
        <IssueProductItem
          key={order._id + product.productId}
          productName={product.fullName}
          unitPrice={product.price}
          quantity={product.volume}
          totalPrice={product.price * product.volume}
          isLast={index === order.products.length - 1}
          isHighlight={issueCases?.some((issueCase) =>
            issueCase.products?.some((p) => p.productId === product.productId),
          )}
        />
      ))}
    </div>
  );
};

export default IssueOrderCard;
export type { IssueOrderCardProps };
